/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/

import { useCallback } from 'react';
import { queryCache, useMutation, useQuery } from 'react-query';

import { ServiceType } from 'shared';

import { UserApiService } from '../services/UserApiService';

export type FavoriteService = {
  id: string;
  name: string;
  type: ServiceType;
  organization: {
    id: string;
    name: string;
  };
  project: {
    id: string;
    name: string;
  };
};

export const useFavoriteServices = () => {
  const { data: favoriteServices = { data: { payload: [] } }, isLoading } = useQuery(
    'favoriteServices',
    async () => {
      return UserApiService.getFavoriteServices();
    },
    {
      staleTime: 5 * 60 * 1000,
    },
  );

  const [addFavoriteService] = useMutation(
    async (serviceId: string) => {
      return UserApiService.addFavoriteService(serviceId);
    },
    {
      onSuccess: () => {
        void queryCache.invalidateQueries('favoriteServices');
      },
      onError: err => {
        console.error('❌ Failed to add favorite service:', err);
      },
    },
  );

  const [removeFavoriteService] = useMutation(
    async (serviceId: string) => {
      return UserApiService.removeFavoriteService(serviceId);
    },
    {
      onSuccess: () => {
        void queryCache.invalidateQueries('favoriteServices');
      },
      onError: err => {
        console.error('❌ Failed to remove favorite service:', err);
      },
    },
  );

  const toggleFavorite = useCallback(
    (event: React.MouseEvent, serviceId: string) => {
      event.stopPropagation();

      const servicesList = favoriteServices?.data?.payload || [];

      if (servicesList.some(fv => fv.id === serviceId)) {
        void removeFavoriteService(serviceId);
      } else {
        void addFavoriteService(serviceId);
      }
    },
    [addFavoriteService, removeFavoriteService, favoriteServices],
  );

  return {
    favoriteServices: favoriteServices?.data?.payload || [],
    toggleFavorite,
    loading: isLoading,
  };
};
